import { AUTH_KEY } from "../consts/auth";
import { logoutUser } from "./auth";
import { getNewRefreshTime, getTokenExpiration } from "./token";
import { refreshToken as doRefreshToken } from "../sign-in/SignIn.state";

// Token refresh orchestrator
// - Ensures only one tab performs a refresh at a time (single-flight)
// - Other tabs wait and then read the latest token from localStorage
// - Uses BroadcastChannel (with storage fallback) to signal start/done/fail

const REFRESH_LOCK = "auth_refresh_lock";
const REFRESH_EVENT = "auth_refresh_event";
const REFRESH_CHANNEL = "auth-refresh";
const LOCK_TTL_MS = 30000; // 30s

// Unique id for this tab (best-effort)
const tabId = (() => {
  try { if (window?.crypto?.randomUUID) return window.crypto.randomUUID(); } catch {}
  return `tab-${Math.random().toString(36).slice(2)}`;
})();

// In-memory waiters within the same tab
let inflightPromise = null;
let resolveInflight = null;

const bc = (() => {
  try { if ("BroadcastChannel" in window) return new BroadcastChannel(REFRESH_CHANNEL); } catch {}
  return null;
})();

// Begin waiting for an in-flight refresh
// Creates a shared promise so concurrent same-tab callers wait behind one refresh
function waitForRefreshStart() {
  if (!inflightPromise) {
    inflightPromise = new Promise((resolve) => { resolveInflight = resolve; });
  }
  return inflightPromise;
}

// Resolve all local waiters
// Clears the shared promise allowing the next refresh cycle to construct a new one
function resolveWaiters(payload) {
  if (resolveInflight) resolveInflight(payload);
  inflightPromise = null;
  resolveInflight = null;
}

// Broadcast an event to other tabs and mirror to storage
// Storage write ensures notification for tabs lacking BroadcastChannel support
function publish(event) {
  const payload = { ...event, at: Date.now() };
  try { if (bc) bc.postMessage(payload); } catch {}
  try { localStorage.setItem(REFRESH_EVENT, JSON.stringify(payload)); } catch {}
}

function onEvent(data) {
  if (!data || typeof data !== "object") return;
  if (data.type === "start") waitForRefreshStart(); // another tab began refresh
  if (data.type === "done" || data.type === "fail") resolveWaiters(data); // refresh finished
}

if (bc) {
  try { bc.onmessage = (e) => onEvent(e?.data); } catch {}
}

try {
  window.addEventListener("storage", (e) => {
    if (e.key !== REFRESH_EVENT) return;
    try { onEvent(JSON.parse(e.newValue || "null")); } catch {}
  });
} catch {}

// Attempt to acquire the cross-tab lock
// Write our ownership if no lock or expired; re-read to confirm ownership
function acquireLock(ttlMs = LOCK_TTL_MS) {
  const now = Date.now();
  let current = null;
  try { current = JSON.parse(localStorage.getItem(REFRESH_LOCK) || "null"); } catch {}
  if (!current || now > Number(current.expiresAt)) {
    const lock = { ownerId: tabId, expiresAt: now + ttlMs };
    try {
      localStorage.setItem(REFRESH_LOCK, JSON.stringify(lock));
      const confirm = JSON.parse(localStorage.getItem(REFRESH_LOCK) || "null");
      if (confirm && confirm.ownerId === tabId) return lock;
    } catch {}
  }
  return null;
}

function releaseLock(id = tabId) {
  try {
    const current = JSON.parse(localStorage.getItem(REFRESH_LOCK) || "null");
    if (current && current.ownerId === id) localStorage.removeItem(REFRESH_LOCK);
  } catch {}
}

// Persist tokens for default caller shape (request interceptor / WS)
// Persist first so immediate retries see latest values
function persistDefault(payload) {
  const token = payload?.token;
  const refresh = payload?.refreshToken;
  const tokenType = payload?.tokenType || "Bearer";
  const expiresIn = Number(payload?.expiresIn) || 0;
  const refreshExpiresIn = Number(payload?.refreshTokenExpireIn) || 0;
  const baseState = payload?.authState || {};

  const authState = {
    ...baseState,
    token,
    refresh_token: refresh,
    token_received_at: new Date().toISOString(),
    token_expired_at: getNewRefreshTime(expiresIn),
  };

  try {
    localStorage.setItem(`${AUTH_KEY}`, token);
    localStorage.setItem(`${AUTH_KEY}_refresh`, refresh);
    localStorage.setItem(`${AUTH_KEY}_refresh_time`, getNewRefreshTime(refreshExpiresIn));
    localStorage.setItem(`${AUTH_KEY}_state`, JSON.stringify(authState));
    localStorage.setItem(`${AUTH_KEY}_type`, tokenType);
  } catch {}

  return { token, refresh, tokenType, expiresIn, refreshExpiresIn, authState };
}

// Persist tokens for react-auth-kit callback shape
function persistAuthKit(payload) {
  const token = payload?.newAuthToken;
  const refresh = payload?.newRefreshToken;
  const tokenType = "Bearer";
  const expiresIn = Number(payload?.newAuthTokenExpireIn) || 0;
  const refreshExpiresIn = Number(payload?.newRefreshTokenExpiresIn) || 0;
  const state = payload?.newAuthUserState || {};

  const authState = { ...state, token, refresh_token: refresh };

  try {
    localStorage.setItem(`${AUTH_KEY}`, token);
    localStorage.setItem(`${AUTH_KEY}_refresh`, refresh);
    localStorage.setItem(`${AUTH_KEY}_refresh_time`, getNewRefreshTime(refreshExpiresIn));
    localStorage.setItem(`${AUTH_KEY}_state`, JSON.stringify(authState));
    localStorage.setItem(`${AUTH_KEY}_type`, tokenType);
  } catch {}

  return { token, refresh, tokenType, expiresIn, refreshExpiresIn, authState };
}

// Read latest tokens from storage (default shape)
// Used by follower tabs to update Authorization headers
function readDefault() {
  try {
    const tokenType = localStorage.getItem(`${AUTH_KEY}_type`) || "Bearer";
    const token = localStorage.getItem(`${AUTH_KEY}`);
    const refresh = localStorage.getItem(`${AUTH_KEY}_refresh`);
    const state = JSON.parse(localStorage.getItem(`${AUTH_KEY}_state`) || "null");
    return {
      authState: state || {},
      token,
      tokenType,
      expiresIn: getTokenExpiration(token),
      refreshToken: refresh,
      refreshTokenExpireIn: getTokenExpiration(refresh),
    };
  } catch { return null; }
}

// Read latest tokens from storage (auth-kit shape)
function readAuthKit() {
  try {
    const token = localStorage.getItem(`${AUTH_KEY}`);
    const refresh = localStorage.getItem(`${AUTH_KEY}_refresh`);
    const state = JSON.parse(localStorage.getItem(`${AUTH_KEY}_state`) || "null");
    return {
      isSuccess: !!token,
      newAuthUserState: state || {},
      newAuthToken: token,
      newAuthTokenExpireIn: getTokenExpiration(token),
      newRefreshToken: refresh,
      newRefreshTokenExpiresIn: getTokenExpiration(refresh),
    };
  } catch { return { isSuccess: false }; }
}

// Leader performs the actual refresh (default shape)
// Others are waiting on the in-flight promise
async function performDefaultAsLeader({ currentAuthState }) {
  publish({ type: "start", ownerId: tabId });
  waitForRefreshStart();
  try {
    const payload = await doRefreshToken({ currentAuthState });
    if (!payload || payload?.isSuccess === false) throw new Error("No payload from refresh");
    const persisted = persistDefault(payload);
    publish({ type: "done", ownerId: tabId });
    resolveWaiters(persisted);
    return payload;
  } catch (err) {
    console.debug('REFTOKEN: Refresh token failed', err);
    publish({ type: "fail", ownerId: tabId, error: String(err) });
    resolveWaiters(null);
    logoutUser();
    throw err;
  } finally {
    releaseLock(tabId);
  }
}

// Leader performs the actual refresh (auth-kit shape)
async function performAuthKitAsLeader({ currentAuthState }) {
  publish({ type: "start", ownerId: tabId });
  waitForRefreshStart();
  try {
    const payload = await doRefreshToken({ isRefresh: true, currentAuthState });
    if (!payload || payload?.isSuccess === false) throw new Error("AuthKit refresh failed");
    persistAuthKit(payload);
    publish({ type: "done", ownerId: tabId });
    resolveWaiters(payload);
    return payload;
  } catch (err) {
    console.debug('REFTOKEN: Refresh token failed', err);
    publish({ type: "fail", ownerId: tabId, error: String(err) });
    resolveWaiters(null);
    logoutUser();
    throw err;
  } finally {
    releaseLock(tabId);
  }
}

// Public API: wait for a fresh token; lead if needed (default shape)
// Returns new creds when leader; returns latest-from-storage when follower
export async function waitForFreshToken({ currentAuthState } = {}) {
  if (inflightPromise) { await inflightPromise; return readDefault(); }
  const lock = acquireLock();
  if (lock) return performDefaultAsLeader({ currentAuthState });
  await waitForRefreshStart();
  return readDefault();
}

// Public API: wait for a fresh token; lead if needed (react-auth-kit shape)
export async function refreshForAuthKit({ currentAuthState } = {}) {
  if (inflightPromise) { await inflightPromise; return readAuthKit(); }
  const lock = acquireLock();
  if (lock) return performAuthKitAsLeader({ currentAuthState });
  await waitForRefreshStart();
  return readAuthKit();
}

export function isRefreshRunning() {
  return !!inflightPromise;
}


